import type {
    CalendarDataProps,
    CompactCalendarProps,
} from '@eventbrite/timed-entry';
import type { SessionResponse } from '../actions/dateSessions';
import type { TemplateProps } from '../containers/template';

export enum AccordionSection {
    date = 'date',
    time = 'time',
    tickets = 'tickets',
}

export enum CalendarType {
    singleDayEvents = 'singleDayEvents',
    compactWithSession = 'compactWithSession',
    compactWithoutSession = 'compactWithoutSession',
    scrollCalendar = 'scrollCalendar',
}

export enum SalesStatus {
    soldOut = 'soldOut',
    onSale = 'onSale',
    notOnSale = 'notOnSale',
}

export enum DiscountType {
    percentage = 'percentage',
    fixed = 'fixed',
}

export interface EventDate {
    /**
     * The date of the event
     */
    date: string;
    checkoutEnabled: boolean;
    soldOut: boolean;
    salesStatus?: SalesStatus;
    highDemand?: boolean;
    /**
     * The unique identifier of the session, present only if calendarType is compactWithSession
     */
    sessionId?: string;
    /**
     * The start date and time of the session in ISO 8601 format, present only if calendarType is compactWithSession
     */
    sessionStartDatetime?: string;
    sessions?: SessionResponse[];
}

export interface UrgencySignal {
    fewTickets: boolean;
    popular: boolean;
    goingFast: boolean;
}

export interface Discount {
    discountType: DiscountType;
    discountValue: number;
}

export interface TimeSlot {
    discount: Discount;
    endDatetime: string;
    id: string;
    salesStatus: SalesStatus;
    startDatetime: string;
    urgencySignals: UrgencySignal;
}

export interface DateApiResponse {
    calendarType: CalendarType;
    eventDates: EventDate[];
}

export interface ApiResponseData {
    dateSection: DateApiResponse;
    timeSection: TimeSlot[];
}

export interface ParentEventInfo {
    id: string;
    startUTCDate: string;
    endUTCDate: string;
}

export type DateAvailabilityStatus =
    | 'available'
    | 'unavailable'
    | 'discount'
    | 'soldOut'
    | 'highDemand';

export type ScrollCalendarData = CalendarDataProps<DateAvailabilityStatus>;
export type CompactCalendarData = CompactCalendarProps['calendarData'];

export interface InfiniteCalendarSeriesNamespace {
    activeSection: AccordionSection | null;
    calendarType: CalendarType;
    calendarDates: EventDate[];
    compactCalendarData: CompactCalendarData;
    eventDateOverride?: string;
    eventStatus?: EventStatus;
    firstMonth: {
        month: number;
        year: number;
    };
    isAppRebrandActive: boolean;
    isEventOnline?: boolean;
    isTicketSectionLoading: boolean;
    isTimeSectionLoading: boolean;
    locale: string;
    parentEventInfo: ParentEventInfo; // original event info, new loaded tickets override redux event info
    scrollCalendarData: ScrollCalendarData;
    selectedDate?: string;
    selectedSessionId?: string;
    selectedTime?: string;
    shouldShowTicketError: boolean;
    shouldShowTimeError: boolean;
    templateProps?: TemplateProps;
    timeSlots: TimeSlot[] | null;
    timezone: string;
}

export interface WithInfiniteCalendarSeriesNamespace {
    infiniteCalendarSeries: InfiniteCalendarSeriesNamespace;
}

export interface TransformedEventDate {
    date: string;
    availability: DateAvailabilityStatus;
}

export enum DateEventStatus {
    available = 'available',
    soldOut = 'soldOut',
    unavailable = 'unavailable',
    highDemand = 'highDemand',
}

export enum EventStatus {
    canceled = 'event_cancelled',
    postponed = 'event_postponed',
    salesEnded = 'tickets_with_sales_ended',
}
